/**
 * Generic bookmarks system with backend API integration
 * Supports items, scenes, and future entity types
 */

import type { EntityType } from '@stw/types';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import {
  addBookmark as apiAddBookmark,
  removeBookmark as apiRemoveBookmark,
} from '@/api/bookmarks';
import { useAuthStore } from '@/stores/auth';

// Separate state for each entity type
const bookmarkedEntities = ref<Map<EntityType, Set<string>>>(
  new Map([
    ['item', new Set()],
    ['scene', new Set()],
  ])
);

export const useBookmarks = (entityType: EntityType) => {
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);

  // Get or create the set for this entity type
  const getBookmarkSet = () => {
    if (!bookmarkedEntities.value.has(entityType)) {
      bookmarkedEntities.value.set(entityType, new Set());
    }
    return bookmarkedEntities.value.get(entityType)!;
  };

  const isBookmarked = (entityId: string): boolean => {
    return getBookmarkSet().has(entityId);
  };

  const toggleBookmark = async (
    entityId: string,
    currentlyBookmarked?: boolean,
    currentBookmarkCount?: number
  ): Promise<{ is_bookmarked: boolean; bookmark_count: number }> => {
    if (!user.value) {
      console.warn('User not authenticated, cannot toggle bookmark');
      return {
        is_bookmarked: false,
        bookmark_count: currentBookmarkCount ?? 0,
      };
    }

    const bookmarkSet = getBookmarkSet();
    // Use passed state if available, otherwise fall back to local state
    const isCurrentlyBookmarked =
      currentlyBookmarked ?? bookmarkSet.has(entityId);
    const count = currentBookmarkCount ?? 0;

    try {
      if (isCurrentlyBookmarked) {
        // Remove from bookmarks
        await apiRemoveBookmark(entityType, entityId);
        bookmarkSet.delete(entityId);
        bookmarkedEntities.value = new Map(bookmarkedEntities.value);
        return { is_bookmarked: false, bookmark_count: Math.max(0, count - 1) };
      } else {
        // Add to bookmarks
        await apiAddBookmark(entityType, entityId);
        bookmarkSet.add(entityId);
        bookmarkedEntities.value = new Map(bookmarkedEntities.value);
        return { is_bookmarked: true, bookmark_count: count + 1 };
      }
    } catch (error) {
      console.error(`Failed to toggle ${entityType} bookmark:`, error);
      throw error;
    }
  };

  const addBookmark = async (entityId: string): Promise<void> => {
    if (!user.value) {
      console.warn('User not authenticated, cannot add bookmark');
      return;
    }

    try {
      await apiAddBookmark(entityType, entityId);
      getBookmarkSet().add(entityId);
      bookmarkedEntities.value = new Map(bookmarkedEntities.value);
    } catch (error) {
      console.error(`Failed to add ${entityType} bookmark:`, error);
      throw error;
    }
  };

  const removeBookmark = async (entityId: string): Promise<void> => {
    if (!user.value) {
      console.warn('User not authenticated, cannot remove bookmark');
      return;
    }

    try {
      await apiRemoveBookmark(entityType, entityId);
      getBookmarkSet().delete(entityId);
      bookmarkedEntities.value = new Map(bookmarkedEntities.value);
    } catch (error) {
      console.error(`Failed to remove ${entityType} bookmark:`, error);
      throw error;
    }
  };

  const syncBookmarksFromItems = (
    items: Array<{ id: string; is_bookmarked: boolean }>
  ): void => {
    if (!user.value) {
      return;
    }

    const bookmarkSet = getBookmarkSet();

    items.forEach((item) => {
      if (item.is_bookmarked) {
        bookmarkSet.add(item.id);
      } else {
        bookmarkSet.delete(item.id);
      }
    });

    bookmarkedEntities.value = new Map(bookmarkedEntities.value);
  };

  const bookmarkedEntityIds = computed(() => Array.from(getBookmarkSet()));

  const bookmarkCount = computed(() => getBookmarkSet().size);

  return {
    isBookmarked,
    toggleBookmark,
    addBookmark,
    removeBookmark,
    syncBookmarksFromItems,
    bookmarkedEntityIds,
    bookmarkCount,
  };
};

// Convenience functions for specific entity types
export const useBookmarkedItems = () => useBookmarks('item');
export const useBookmarkedScenes = () => useBookmarks('scene');
